

import React, { useState } from 'react';
import {
    type ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
    type SortingState,
    type ColumnFiltersState,
    type VisibilityState,
    type Row,
    type Column,
    type HeaderGroup,
    type Header,
    type Cell,
} from "@tanstack/react-table";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/shared/ui/table";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "@/shared/ui/dropdown-menu";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { MoreHorizontal, Settings2 } from "lucide-react";
import { TruckDriver, TruckDriverInsert, TruckDriverUpdate } from '@/types';
import TruckDriverFormDialog from './TruckDriverFormDialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { columns as truckDriverColumns } from './TruckDriversColumns';

// トラック運転手一覧のprops
interface TruckDriversTableProps {
    data: TruckDriver[];
    isLoading: boolean;
    onEdit: (id: string, data: TruckDriverUpdate) => Promise<boolean>;
    onDelete: (id: string) => Promise<boolean>;
    checkDriverInUse: (driverId: string) => Promise<boolean>;
}

/**
 * トラック運転手一覧
 * @param data トラック運転手データ
 * @param isLoading ローディング中かどうか
 * @param onEdit トラック運転手の編集処理
 * @param onDelete トラック運転手の削除処理
 * @param checkDriverInUse トラック運転手の使用状況を確認する関数
 * @returns トラック運転手一覧
 */
export default function TruckDriversTable({ data, isLoading, onEdit, onDelete, checkDriverInUse }: TruckDriversTableProps) {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [rowSelection, setRowSelection] = useState({});

    const [editingDriver, setEditingDriver] = useState<TruckDriver | null>(null);
    const [deletingDriverId, setDeletingDriverId] = useState<string | null>(null);
    const [isDriverInUse, setIsDriverInUse] = useState<boolean>(false);
    const [checkingUsage, setCheckingUsage] = useState<boolean>(false);

    /**
     * 列定義に行アクションのコールバックを渡すための meta を設定
     * @returns 列定義
     */
    const handleDeleteConfirm = React.useCallback(async (id: string) => {
        setDeletingDriverId(id);
        setCheckingUsage(true);

        try {
            const inUse = await checkDriverInUse(id);
            setIsDriverInUse(inUse);
        } catch (error) {
            console.error("Failed to check driver usage:", error);
            setIsDriverInUse(false);
        } finally {
            setCheckingUsage(false);
        }
    }, [checkDriverInUse]);

    const columns = React.useMemo<ColumnDef<TruckDriver>[]>(() => truckDriverColumns.map(col => {
        if (col.id === 'actions') {
            return {
                ...col,
                cell: ({ row }: { row: Row<TruckDriver> }) => {
                    const driver = row.original;
                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                    <span className="sr-only">Open menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>操作</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => navigator.clipboard.writeText(driver.id)}>
                                    IDをコピー
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setEditingDriver(driver)}>
                                    編集
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteConfirm(driver.id)} className="text-destructive">
                                    削除
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                }
            };
        }
        return col;
    }), [handleDeleteConfirm]);

    const table = useReactTable<TruckDriver>({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    /**
     * トラック運転手の削除確認処理
     * @param id トラック運転手ID
     */
    // duplicate removed

    /**
     * トラック運転手の削除処理
     */
    const handleDelete = async () => {
        if (deletingDriverId !== null) {
            const success = await onDelete(deletingDriverId);
            if (success) {
                setDeletingDriverId(null);
                setIsDriverInUse(false);
            }
        }
    };

    /**
     * トラック運転手の更新処理
     * @param formData トラック運転手データ
     * @returns 更新処理の成否
     */
    const handleUpdateSubmit = async (formData: TruckDriverInsert | TruckDriverUpdate): Promise<boolean> => {
        if (editingDriver && 'id' in formData) {
            const success = await onEdit(editingDriver.id, formData);
            if (success) {
                setEditingDriver(null);
                return true;
            }
        } else if (!editingDriver) {
            console.error("handleUpdateSubmit called without editingDriver");
        } else {
            console.error("handleUpdateSubmit called with data missing id in update mode");
        }
        return false;
    };

    /**
     * トラック運転手一覧の表示
     * @returns トラック運転手一覧
     */
    if (isLoading) {
        return (
            <div className="rounded-md border p-4">
                <div className="flex items-center py-4">
                    <div className="h-10 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                    <div className="ml-auto h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
                </div>
                <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="h-12 bg-gray-200 rounded animate-pulse"></div>
                    ))}
                </div>
                <div className="flex items-center justify-end space-x-2 py-4">
                    <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
                    <div className="h-10 bg-gray-200 rounded w-20 animate-pulse"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 py-3">
                <Input
                    placeholder="氏名で検索..."
                    value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
                    onChange={(event) =>
                        table.getColumn("name")?.setFilterValue(event.target.value)
                    }
                    className="flex-1 sm:max-w-sm"
                />
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full sm:w-auto sm:ml-auto" size="sm">
                            <span className="hidden sm:inline">表示列</span>
                            <span className="sm:hidden">列設定</span>
                            <Settings2 className="ml-2 h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {table
                            .getAllColumns()
                            .filter((column) => column.getCanHide())
                            .map((column: Column<TruckDriver, unknown>) => {
                                return (
                                    <DropdownMenuCheckboxItem
                                        key={column.id}
                                        className="capitalize"
                                        checked={column.getIsVisible()}
                                        onCheckedChange={(value) =>
                                            column.toggleVisibility(!!value)
                                        }
                                    >
                                        {column.id === 'name' ? '氏名' :
                                            column.id === 'daily_rate' ? '日単価 (円)' :
                                                column.id === 'created_at' ? '登録日時' :
                                                    column.id === 'updated_at' ? '更新日時' :
                                                        column.id === 'select' ? '選択' :
                                                            column.id === 'actions' ? '操作' : column.id}
                                    </DropdownMenuCheckboxItem>
                                );
                            })}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup: HeaderGroup<TruckDriver>) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header: Header<TruckDriver, unknown>) => {
                                    return (
                                        <TableHead key={header.id} style={{ width: header.getSize() !== 0 ? header.getSize() : undefined }}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row: Row<TruckDriver>) => {
                                return (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                    >
                                        {row.getVisibleCells().map((cell: Cell<TruckDriver, unknown>) => {
                                            return (
                                                <TableCell key={cell.id} style={{ width: cell.column.getSize() !== 0 ? cell.column.getSize() : undefined }}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="h-24 text-center"
                                >
                                    登録されているトラック運転手がありません。
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
                <div className="flex-1 text-sm text-muted-foreground">
                    {table.getFilteredSelectedRowModel().rows.length} 件選択中 / 全 {table.getFilteredRowModel().rows.length} 件
                </div>
                <div className="space-x-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        前へ
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        次へ
                    </Button>
                </div>
            </div>

            <TruckDriverFormDialog
                isOpen={!!editingDriver}
                onClose={() => setEditingDriver(null)}
                onSubmit={handleUpdateSubmit}
                defaultValues={editingDriver}
                isUpdate={true}
            />

            <AlertDialog open={deletingDriverId !== null} onOpenChange={(open) => !open && setDeletingDriverId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>削除確認</AlertDialogTitle>
                        <AlertDialogDescription>
                            {checkingUsage ? (
                                "使用状況を確認中..."
                            ) : isDriverInUse ? (
                                <>
                                    <span className="text-red-600 font-medium">[警告]</span><br />
                                    ID: {deletingDriverId} のドライバーは現在プロジェクトに割り当てられています。<br />
                                    削除を実行すると、データベースエラーが発生する可能性があります。<br />
                                    先に割り当てを解除することをお勧めします。
                                </>
                            ) : (
                                <>
                                    ID: {deletingDriverId} のドライバーデータを本当に削除しますか？<br />
                                    この操作は元に戻せません。
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeletingDriverId(null)}>キャンセル</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive hover:bg-destructive/90"
                            disabled={checkingUsage}
                        >
                            {checkingUsage ? "確認中..." : "削除"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}